/**
 * Resolve which content field a global-search term matched, so the editor can scroll to and
 * highlight it (see lib/forms/deepLinkFocus). The `/search` API returns no field-level data for
 * content hits — only the entity — so we re-derive the field client-side by scanning the loaded
 * document for the searched text. Best-effort: if nothing matches we just don't focus anything.
 */

/** Sentinel path for the entity's identity title (rendered outside the schema-driven doc form). */
export const TITLE_FOCUS_PATH = '__title';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** An `{ en, ar }` localized leaf — rendered as a single field, so it focuses at its own path. */
function isI18nLeaf(v: unknown): v is { en: string; ar: string } {
  return (
    isPlainObject(v) &&
    typeof v.en === 'string' &&
    typeof v.ar === 'string' &&
    Object.keys(v).every((k) => k === 'en' || k === 'ar')
  );
}

function includesCI(haystack: string, needle: string): boolean {
  return haystack.toLowerCase().includes(needle);
}

/**
 * Depth-first search for the first field whose text contains `query`, returning its dot-path in
 * the convention {@link SchemaForm} addresses fields by: object keys as `a.b`, array indices as
 * `a.0`, and `{ en, ar }` leaves at their own path (never `a.en`). Returns `null` if no field
 * matches or the query is empty.
 */
export function findFieldPath(doc: unknown, query: string): string | null {
  const needle = query.trim().toLowerCase();
  if (!needle) return null;

  let found: string | null = null;
  const visit = (val: unknown, path: string): void => {
    if (found != null) return;
    if (val == null) return;
    if (isI18nLeaf(val)) {
      if (includesCI(`${val.en} ${val.ar}`, needle)) found = path;
      return;
    }
    if (typeof val === 'string') {
      if (includesCI(val, needle)) found = path;
      return;
    }
    if (Array.isArray(val)) {
      for (let i = 0; i < val.length && found == null; i++) {
        visit(val[i], path ? `${path}.${i}` : String(i));
      }
      return;
    }
    if (isPlainObject(val)) {
      for (const [k, v] of Object.entries(val)) {
        visit(v, path ? `${path}.${k}` : k);
        if (found != null) return;
      }
    }
  };
  visit(doc, '');
  return found;
}

/** Does the entity title contain the searched term? Title lives outside the doc form. */
export function titleMatches(title: unknown, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return false;
  if (isI18nLeaf(title)) return includesCI(`${title.en} ${title.ar}`, needle);
  if (typeof title === 'string') return includesCI(title, needle);
  return false;
}

/**
 * The deep-link focus path for a content editor arriving from search: the matched doc field, or
 * the title sentinel when only the title matched. Prefers the doc body (the title is already
 * shown in the page header and the search dropdown). `null` ⇒ nothing to focus.
 */
export function resolveFocusPath(
  title: unknown,
  doc: unknown,
  query: string | null | undefined,
): string | null {
  if (!query || !query.trim()) return null;
  const inDoc = findFieldPath(doc, query);
  if (inDoc != null) return inDoc;
  return titleMatches(title, query) ? TITLE_FOCUS_PATH : null;
}
